import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut,
  type User,
  type Unsubscribe
} from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { getKajikajyFirebaseClient } from "./firebaseClient";
import type {
  InvoiceIntent,
  InvoiceStatus,
  KajikajyPlanId,
  PaymentIntegrationMode,
  PaymentProviderId,
  SubscriptionStatus
} from "@kajikajy/shared";

export interface SubscriptionView {
  readonly activatedAt: string | null;
  readonly expiresAt: string | null;
  readonly graceUntil: string | null;
  readonly invoiceId: string | null;
  readonly paymentProvider: PaymentProviderId | null;
  readonly paymentReference: string | null;
  readonly phoneNumber: string | null;
  readonly planId: KajikajyPlanId | null;
  readonly status: SubscriptionStatus | null;
  readonly uid: string;
}

export interface GetSubscriptionResult {
  readonly ok: boolean;
  readonly subscription: SubscriptionView | null;
}

export interface SendSmsIfPaidResult {
  readonly ok: boolean;
  readonly queued: boolean;
  readonly reason?: string;
}

export type SendSmsIfPaidPurpose = "essorRecovery" | "firstSubscriptionAuth";

export interface AccountCustomerView {
  readonly businessName: string | null;
  readonly displayName: string | null;
  readonly email: string | null;
  readonly phoneNumber: string | null;
}

export interface AccountInvoiceView {
  readonly amountMGA: number;
  readonly createdAt: string | null;
  readonly currency: "MGA";
  readonly expiresAt: string | null;
  readonly id: string;
  readonly intent: InvoiceIntent;
  readonly invoiceNumber: string | null;
  readonly paidAt: string | null;
  readonly paymentProvider: PaymentProviderId | null;
  readonly paymentReference: string | null;
  readonly planId: KajikajyPlanId;
  readonly status: InvoiceStatus;
}

export interface AccountDashboardView {
  readonly customer: AccountCustomerView | null;
  readonly invoices: readonly AccountInvoiceView[];
  readonly subscription: SubscriptionView;
}

export interface GetAccountDashboardResult {
  readonly ok: boolean;
  readonly account: AccountDashboardView | null;
}

export interface SignInWithPhonePinResult {
  readonly customToken: string;
  readonly ok: boolean;
}

export interface ActivateLegacyPhonePinPayload {
  readonly activationToken: string;
  readonly phoneNumber: string;
  readonly pin: string;
  readonly pinConfirmation: string;
}

export interface ActivateLegacyPhonePinResult {
  readonly ok: boolean;
}

export interface GetInvoicesResult {
  readonly ok: boolean;
  readonly invoices: readonly AccountInvoiceView[];
}

export interface CreateAccountPaymentSessionPayload {
  readonly intent: Extract<InvoiceIntent, "renewal" | "upgrade">;
  readonly phoneNumber: string | null;
  readonly planId: KajikajyPlanId;
}

export interface CreateAccountPaymentSessionResult {
  readonly amountMGA: number;
  readonly checkoutUrl: string | null;
  readonly currency: "MGA";
  readonly expiresAt: string;
  readonly integrationMode: PaymentIntegrationMode;
  readonly intent: InvoiceIntent;
  readonly invoiceId: string;
  readonly ok: boolean;
  readonly paymentIntentId: string;
  readonly paymentReference: string;
  readonly provider: PaymentProviderId;
  readonly providerLabel: string;
  readonly status: InvoiceStatus;
  readonly statusToken: string;
}

export interface GetPaymentStatusPayload {
  readonly paymentIntentId: string;
  readonly paymentReference: string;
  readonly statusToken: string;
}

export interface GetPaymentStatusResult {
  readonly ok: boolean;
  readonly invoiceId: string | null;
  readonly paymentIntentId: string | null;
  readonly status: InvoiceStatus;
  readonly paidAt: string | null;
  readonly expiresAt: string | null;
}

function requireFirebaseClient() {
  const client = getKajikajyFirebaseClient();
  if (!client) {
    throw new Error("Firebase web config is missing.");
  }
  return client;
}

export function observeAccountUser(onChange: (user: User | null) => void): Unsubscribe {
  const client = requireFirebaseClient();
  return onAuthStateChanged(client.auth, onChange);
}

export async function signInAccount(email: string, password: string): Promise<User> {
  const client = requireFirebaseClient();
  const credential = await signInWithEmailAndPassword(client.auth, email.trim(), password);
  return credential.user;
}

export async function signOutAccount(): Promise<void> {
  const client = requireFirebaseClient();
  await signOut(client.auth);
}

export async function getSubscription(): Promise<GetSubscriptionResult> {
  const client = requireFirebaseClient();
  const callable = httpsCallable<Record<string, never>, GetSubscriptionResult>(
    client.functions,
    "getSubscription"
  );
  const result = await callable({});
  return result.data;
}

export async function getAccountDashboard(): Promise<GetAccountDashboardResult> {
  const client = requireFirebaseClient();
  const callable = httpsCallable<Record<string, never>, GetAccountDashboardResult>(
    client.functions,
    "getAccountDashboard"
  );
  const result = await callable({});
  return result.data;
}

export async function signInAccountWithPhonePin(
  phoneNumber: string,
  pin: string
): Promise<User> {
  const client = requireFirebaseClient();
  const callable = httpsCallable<
    { phoneNumber: string; pin: string },
    SignInWithPhonePinResult
  >(
    client.functions,
    "signInWithPhonePin"
  );
  const result = await callable({ phoneNumber, pin });
  if (!result.data.customToken) {
    throw new Error("The phone sign-in response did not include a custom token.");
  }
  const credential = await signInWithCustomToken(
    client.auth,
    result.data.customToken
  );
  return credential.user;
}

export async function activateLegacyPhonePin(
  payload: ActivateLegacyPhonePinPayload
): Promise<ActivateLegacyPhonePinResult> {
  const client = requireFirebaseClient();
  const callable = httpsCallable<
    ActivateLegacyPhonePinPayload,
    ActivateLegacyPhonePinResult
  >(client.functions, "activateLegacyPhonePin");
  const result = await callable(payload);
  return result.data;
}

export async function getInvoices(): Promise<GetInvoicesResult> {
  const client = requireFirebaseClient();
  const callable = httpsCallable<Record<string, never>, GetInvoicesResult>(
    client.functions,
    "getInvoices"
  );
  const result = await callable({});
  return result.data;
}

export async function createAccountPaymentSession(
  payload: CreateAccountPaymentSessionPayload
): Promise<CreateAccountPaymentSessionResult> {
  const client = requireFirebaseClient();
  const callable = httpsCallable<CreateAccountPaymentSessionPayload, CreateAccountPaymentSessionResult>(
    client.functions,
    "createAccountPaymentSession"
  );
  const result = await callable(payload);
  return result.data;
}

export async function getPaymentStatus(
  payload: GetPaymentStatusPayload
): Promise<GetPaymentStatusResult> {
  const client = requireFirebaseClient();
  const callable = httpsCallable<GetPaymentStatusPayload, GetPaymentStatusResult>(
    client.functions,
    "getPaymentStatus"
  );
  const result = await callable(payload);
  return result.data;
}

export async function sendSmsIfPaid(
  purpose: SendSmsIfPaidPurpose
): Promise<SendSmsIfPaidResult> {
  const client = requireFirebaseClient();
  const callable = httpsCallable<{ purpose: SendSmsIfPaidPurpose }, SendSmsIfPaidResult>(
    client.functions,
    "sendSmsIfPaid"
  );
  const result = await callable({ purpose });
  return result.data;
}
